import { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import { CategoryFAQ } from '@/components/ui/CategoryFAQ';
import { inviteApi } from '@/services/api';

export const FAQPage: FC = () => {
  const [inviteCode, setInviteCode] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [copied, setCopied] = useState(false);

  const handleGetInviteCode = async () => {
    setInviteLoading(true);
    setInviteError('');
    setCopied(false);
    try {
      const res = await inviteApi.generatePublic();
      setInviteCode(res.code);
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : '获取邀请码失败，请稍后重试');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCopyInviteCode = async () => {
    if (!inviteCode) return;
    try {
      await navigator.clipboard.writeText(inviteCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setInviteError('复制失败，请手动复制');
    }
  };

  return (
    <>
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
        <Container>
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              常见问题
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              为您解答关于白茆AI服务的常见疑问，帮助您更好地了解和使用我们的产品
            </p>
            <div className="mt-8 flex justify-center">
              <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500">
                <div className="flex items-center space-x-2">
                  <span className="text-red-500">🚨</span>
                  <span>异常情况</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-blue-500">💡</span>
                  <span>实用技巧</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-green-500">💬</span>
                  <span>售前问题</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-purple-500">📖</span>
                  <span>AI知识点</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-orange-500">🎓</span>
                  <span>学习教程</span>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* 如何查看额度 */}
      <section className="pt-8 pb-0 bg-white">
        <Container>
          <div className="bg-white rounded-lg shadow-lg p-6 md:p-8 border-2 border-blue-200">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-4xl">💰</span>
              <h3 className="text-2xl font-bold text-gray-900">
                如何查看自己的额度?
              </h3>
            </div>
            <div className="text-gray-600 leading-relaxed space-y-4">
              <p>第一步点击右上角注册并登录账号，第二步拉到下面选择对应节点输入您的apiKey</p>
              <p>注册账号必须填写邀请码，可点击下方按钮领取：</p>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <Button
                  type="button"
                  onClick={handleGetInviteCode}
                  disabled={inviteLoading}
                  className="bg-blue-600 text-white hover:bg-blue-700"
                >
                  {inviteLoading ? '获取中…' : '获取邀请码'}
                </Button>
                {inviteCode && (
                  <div className="flex items-center gap-2">
                    <code className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-md text-blue-800 font-mono tracking-wider">
                      {inviteCode}
                    </code>
                    <Button type="button" variant="outline" size="sm" onClick={handleCopyInviteCode}>
                      {copied ? '已复制' : '复制'}
                    </Button>
                  </div>
                )}
              </div>
              {inviteError && <p className="text-sm text-red-500">{inviteError}</p>}
            </div>
          </div>
        </Container>
      </section>

      {/* AI知识点入口提示 */}
      <section className="py-8 bg-gradient-to-r from-purple-50 to-blue-50">
        <Container>
          <div className="bg-white rounded-lg shadow-lg p-6 md:p-8 border-2 border-purple-200">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-4xl">🎓</span>
                  <h3 className="text-2xl font-bold text-gray-900">
                    想系统学习AI编程知识？
                  </h3>
                </div>
                <p className="text-gray-600 mb-4">
                  访问我们的<strong className="text-purple-600">AI编程训练营</strong>，
                  这里有完整的学习路径、丰富的知识点讲解和实战练习，
                  帮助你从零掌握AI辅助编程技能！
                </p>
                <div className="flex flex-wrap gap-2 text-sm">
                  <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full">
                    📖 10+ 核心概念详解
                  </span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full">
                    💻 16+ 实战任务
                  </span>
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full">
                    🎯 4条完整学习路径
                  </span>
                  <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full">
                    ✨ 游戏化学习体验
                  </span>
                </div>
              </div>
              <div>
                <Button
                  as={Link}
                  to="/learning"
                  size="lg"
                  className="bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 whitespace-nowrap"
                >
                  <span className="mr-2">🚀</span>
                  开启学习之旅
                </Button>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* FAQ Content */}
      <section className="py-16 bg-white">
        <Container>
          <CategoryFAQ />
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-accent-500 text-white">
        <Container>
          <div className="text-center">
            <div className="text-6xl mb-6">🤝</div>
            <h2 className="text-3xl font-bold mb-4">
              找不到您想要的答案？
            </h2>
            <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
              我们的专业技术团队和客服随时为您提供一对一的专业解答和技术支持
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                as={Link}
                to="/contact"
                size="lg"
                className="bg-white text-primary-600 hover:bg-gray-100 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
              >
                <span className="mr-2">💬</span>
                联系客服咨询
              </Button>
              <Button
                as={Link}
                to="/pricing"
                variant="outline"
                size="lg"
                className="border-white text-white hover:bg-white hover:text-primary-600 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
              >
                <span className="mr-2">💰</span>
                查看定价方案
              </Button>
            </div>
            <div className="mt-8 text-primary-200 text-sm">
              <p>⏰ 工作时间：周一至周五 9:00-18:00 | 📧 邮箱：386740421@qq.com</p>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
};